import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Search,
  ShieldCheck,
  Gauge,
  AlertTriangle,
  Wrench,
  Phone,
  Mail,
  CheckCircle2,
  XCircle,
  Activity,
  Flame,
  X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const ACCENT = "#F59E0B"; // walnut/amber

const PILLARS = [
  {
    icon: Sparkles,
    title: "Mechanikus dióhéj-koptatás",
    text:
      "Speciális dióhéj-granulátum nyomással szóródik a szívószelepekre — feloldja a kemény koksz-lerakódást anélkül, hogy a fémet sértené.",
  },
  {
    icon: ShieldCheck,
    title: "Minden szelep külön",
    text:
      "Hengerenként, szelepenként végezzük — egyik henger sem marad ki. Soha nem kerül granulátum a hengerbe (külön porszívózás közben).",
  },
  {
    icon: Gauge,
    title: "Vissza a gyári teljesítmény",
    text:
      "Tiszta szívóoldal = tiszta áramlás = vissza a gyári lóerő, nyomaték, fogyasztás. A motor érezhetően frissül.",
  },
  {
    icon: ShieldCheck,
    title: "Vegyszermentes",
    text:
      "Sem maró vegyszer, sem nyitott motorbontás. A dióhéj környezetbarát, biológiailag lebomlik — és nem reagál a fémmel.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Szívócsonk leszerelése",
    text:
      "Kiépítjük a szívócsonkot a motorról, hogy hozzáférjünk a szívószelepekhez. Ez a leghosszabb lépés.",
  },
  {
    n: "02",
    title: "Tömítés + endoszkóp",
    text:
      "A henger felé adapter-tömítéssel zárjuk a furatot. Endoszkóppal dokumentáljuk a koksz-állapotot — ELŐTTE-fotó készül.",
  },
  {
    n: "03",
    title: "Dióhéj-fúvatás",
    text:
      "Speciális gép nyomással fújja a dióhéj-granulátumot. Egyszerre szippantja vissza, így nem szóródik szét. Kemény koksz cserépdarabokra esik.",
  },
  {
    n: "04",
    title: "Tisztítás + visszaszerelés",
    text:
      "Maradék granulátum porszívózása, szívócsonk visszaszerelése új tömítésekkel. Próbaút + adaptáció reset ISTA-val.",
  },
];

const WHEN = [
  {
    icon: Activity,
    title: "80 000 – 120 000 km",
    text:
      "Direkt befecskendezésű (FSI/TFSI/TSI/N20/N43/N53/N54/N55) motorok klasszikus problémája — itt szinte garantált.",
  },
  {
    icon: AlertTriangle,
    title: "Tüneti jelek",
    text:
      "Rezgés alapjáraton, gyenge teljesítmény, többletfogyasztás, hibakód (P0300, P0301-P0306), gyertya gyors elhasználódás.",
  },
  {
    icon: Flame,
    title: "Megelőzés",
    text:
      "Olcsóbb, mint egy szelepfelújítás vagy hengerfej-felújítás. 2–3 órás munka helyett több százezres javítás.",
  },
];

const COMPARISON = {
  vegyszer: [
    { good: false, text: "Vegyszer felfolyik a hengerbe is" },
    { good: false, text: "Kemény koksz-réteget nem oldja fel" },
    { good: false, text: "Csak felületen tisztít, gyökeret nem szed" },
    { good: false, text: "Olajba keveredhet — olajcserét igényel" },
    { good: false, text: "Hibakódot törli, de a problémát nem oldja meg" },
  ],
  diohej: [
    { good: true, text: "100% mechanikus tisztítás — gyökerig" },
    { good: true, text: "Granulátum nem jut a hengerbe (szippantott rendszer)" },
    { good: true, text: "Kemény koksz-réteg darabokban leesik" },
    { good: true, text: "Vegyszermentes, környezetbarát eljárás" },
    { good: true, text: "Mérhető teljesítménynövekedés a próbaúton" },
  ],
};

const FAQ = [
  {
    q: "Milyen motoroknak van rá szüksége?",
    a: "Direkt befecskendezésű benzin motoroknak: BMW N20/N43/N53/N54/N55/N63, B48/B58, VAG TFSI/TSI (1.4 / 1.8 / 2.0), Mercedes M271/M274. Ezeknél a benzin nem mossa át a szívószelepet, így a kartergáz olajpárája odakokszosodik.",
  },
  {
    q: "Tönkremegy a motor a granulátumtól?",
    a: "Nem. A dióhéj puhább, mint az alumínium — nem sérti a szelepet. A speciális gép vákuummal egyszerre szippantja is vissza, így nem jut a hengerbe. Soha nem dolgozunk nyitott szelep mellett.",
  },
  {
    q: "Miért nem elég egy adalékos benzintartály-tisztító?",
    a: "Mert ezek a motorok direkt befecskendezésűek — a benzin a hengerbe megy, nem a szívószelepre. Az adalék soha nem éri el a problémás területet. Mechanikus tisztítás nélkül nincs megoldás.",
  },
  {
    q: "Mennyi idő egy dióhéjas tisztítás?",
    a: "Motortól függően 2–4 óra. A 4-hengeres N20/N43/B48 motorok átlag 2,5 óra, a 6-hengeres N54/N55/B58 motorok 3,5–4 óra.",
  },
  {
    q: "Mennyibe kerül?",
    a: "A kiindulóár 79 000 Ft-tól, a pontos ár motorfüggő (4 vagy 6 henger, hozzáférhetőség). Hívjon vagy küldjön üzenetet — autója adatainak ismeretében pontos kalkulációt adunk.",
  },
];

export default function DiohejasTisztitasPage() {
  const [lightboxSrc, setLightboxSrc] = React.useState(null);

  const closeLightbox = React.useCallback(() => setLightboxSrc(null), []);

  React.useEffect(() => {
    if (!lightboxSrc) return;
    const handler = (e) => e.key === "Escape" && closeLightbox();
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [lightboxSrc, closeLightbox]);

  useDocumentMeta({
    title:
      "Dióhéjas szelep-tisztítás Debrecen — BMW N54/N55/B58 koksz-mentesítés | Autó-Szerelem",
    description:
      "Szakszerű dióhéjas szívószelep-tisztítás Debrecenben. Direkt befecskendezésű BMW (N20/N43/N54/N55/B58), VAG TFSI/TSI motorok koksz-mentesítése — vegyszermentes, mechanikus eljárás. Időpontfoglalás: +36 30 863 7476.",
    path: "/diohejas-tisztitas",
  });

  React.useEffect(() => {
    const id = "diohej-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Dióhéjas szelep-tisztítás",
        name: "Direkt befecskendezésű motorok szívószelep-tisztítása dióhéj-fúvatással",
        provider: {
          "@type": "AutoRepair",
          name: "Autó-Szerelem — Séra Autószervíz",
          telephone: "+36-30-863-7476",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Vadvirág utca 15/D — 3. kapu",
            addressLocality: "Debrecen",
            postalCode: "4031",
            addressCountry: "HU",
          },
        },
        areaServed: { "@type": "City", name: "Debrecen" },
        url: "https://autoszerelem.hu/diohejas-tisztitas",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="diohej-page">
      <Navbar />

      {/* HERO */}
      <section
        className="relative pt-24 sm:pt-28 isolate overflow-hidden"
        data-testid="diohej-hero-section"
      >
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse at 75% 25%, ${ACCENT}40 0%, rgba(10,10,10,0) 55%), radial-gradient(ellipse at 15% 75%, rgba(120,53,15,0.25) 0%, rgba(10,10,10,0) 50%), #0A0A0A`,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
              backgroundSize: "16px 16px",
            }}
          />
          <Sparkles
            className="absolute -right-12 -top-8 w-[36rem] h-[36rem] text-white/[0.04] hidden lg:block"
            strokeWidth={0.6}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-16 sm:pt-6 sm:pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-6 transition-colors"
            data-testid="diohej-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-4 mb-8">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.72rem] font-bold uppercase tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Walnut Blasting / Debrecen
            </span>
          </div>

          <div
            className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 border border-white/15 bg-white/5 backdrop-blur"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            <span
              className="text-[0.65rem] font-black uppercase tracking-[0.28em] text-white"
            >
              Dióhéj-fúvatás
            </span>
            <span className="w-1 h-1 bg-white/30" />
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-400">
              BMW N54 · N55 · B58 · TFSI · TSI
            </span>
          </div>

          <h1
            className="font-black uppercase leading-[0.92] tracking-[-0.01em] text-white max-w-5xl"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2rem, 6.5vw, 6rem)",
            }}
            data-testid="diohej-headline"
          >
            Direkt befecskendezésű motorok
            <br />
            <span style={{ color: ACCENT }}>koksz-mentesítése</span>.
          </h1>

          <p
            className="mt-8 max-w-2xl text-zinc-300/90 text-base sm:text-lg leading-relaxed"
            data-testid="diohej-subheadline"
          >
            A BMW, VW, Audi modern motorok klasszikus problémája: a kartergáz
            olajpárája odakokszosodik a szívószelepekre. <span className="text-white font-semibold">Egyetlen
            megoldás</span> a mechanikus dióhéj-fúvatás — vegyszermentes,
            környezetbarát, gyári állapotot visszaadó tisztítás.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/?service=Teljesk%C3%B6r%C5%B1%20szerviz#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="diohej-cta-booking"
            >
              Időpontfoglalás <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="diohej-cta-phone"
            >
              <Phone className="w-4 h-4" /> +36 30 863 7476
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { k: "2–4 óra", v: "Munkaidő" },
              { k: "Vegyszermentes", v: "Tiszta eljárás" },
              { k: "Hengerenként", v: "Részletes munka" },
              { k: "ISTA reset", v: "Adaptáció" },
            ].map((s, i) => (
              <div
                key={s.v}
                className="bg-[#0d0d0d] px-5 py-5"
                data-testid={`diohej-fact-${i}`}
              >
                <div
                  className="text-xl sm:text-2xl font-black uppercase text-white tracking-tight"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {s.k}
                </div>
                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDY — Before/After */}
      <section
        id="diohej-munkaink"
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="diohej-case-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span
                className="text-[0.7rem] font-bold uppercase tracking-[0.32em]"
                style={{ fontFamily: "Barlow Condensed", color: ACCENT }}
              >
                Munkáink — Esettanulmány
              </span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Szívószelep-tisztítás
                <br />
                <span style={{ color: ACCENT }}>előtte / utána.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              Saját műhelyünkben dokumentált munka. A különbség olyan
              látványos, hogy nem kell magyaráznunk — a fotók mindent
              elmondanak.
            </p>
          </div>

          {/* BEFORE — full-width hero */}
          <button
            type="button"
            onClick={() => setLightboxSrc("/case-studies/diohej/diohej-elotte.jpg")}
            className="group relative w-full aspect-[16/9] bg-[#101010] border border-white/5 hover:border-[#E1132C]/60 overflow-hidden text-left transition-all cursor-zoom-in mb-4"
            data-testid="diohej-case-before"
            aria-label="Erős koksz-lerakódás a szívószelepeken — nagyítás"
          >
            <img
              src="/case-studies/diohej/diohej-elotte.jpg"
              alt="Erős koksz-lerakódás a szívószelepeken — előtte"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-black/60" />
            <div className="absolute top-5 left-5">
              <span
                className="inline-flex items-center gap-2 bg-[#E1132C]/95 text-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.22em]"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Előtte
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
              <h3
                className="font-black uppercase text-white leading-[0.95] max-w-2xl"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(1.5rem, 3vw, 2.5rem)",
                }}
              >
                Súlyos koksz-réteg a szívószelepeken
              </h3>
              <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-xl">
                Vastag, kemény fekete kéreg lepi a szívóoldalt — ez akadályozza
                a levegő áramlását, csökkenti a teljesítményt, növeli a
                fogyasztást, sőt hibakódot is okoz.
              </p>
            </div>
          </button>

          {/* AFTER — 2 photos grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setLightboxSrc("/case-studies/diohej/diohej-utana-szivocsonk.jpg")}
              className="group relative aspect-[4/3] bg-[#101010] border border-white/5 hover:border-[#F59E0B]/60 overflow-hidden text-left transition-all cursor-zoom-in"
              data-testid="diohej-case-after-1"
              aria-label="Tisztított szívócsonk-furat — nagyítás"
            >
              <img
                src="/case-studies/diohej/diohej-utana-szivocsonk.jpg"
                alt="Tisztított szívócsonk-furat dióhéj-fúvatás után"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute top-5 left-5">
                <span
                  className="inline-flex items-center gap-2 text-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.22em]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    backgroundColor: ACCENT,
                  }}
                >
                  Utána
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4
                  className="font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(1.25rem, 2.4vw, 1.75rem)",
                  }}
                >
                  Csillogó szívóport
                </h4>
                <p className="mt-2 text-sm text-zinc-300">
                  Gyári fémfelület — sehol koksz, akadálymentes áramlás.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setLightboxSrc("/case-studies/diohej/diohej-utana-szelep.jpg")}
              className="group relative aspect-[4/3] bg-[#101010] border border-white/5 hover:border-[#F59E0B]/60 overflow-hidden text-left transition-all cursor-zoom-in"
              data-testid="diohej-case-after-2"
              aria-label="Tisztított szívószelep közelről — nagyítás"
            >
              <img
                src="/case-studies/diohej/diohej-utana-szelep.jpg"
                alt="Tisztított szívószelep közelről dióhéj-fúvatás után"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute top-5 left-5">
                <span
                  className="inline-flex items-center gap-2 text-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.22em]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    backgroundColor: ACCENT,
                  }}
                >
                  Utána
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4
                  className="font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(1.25rem, 2.4vw, 1.75rem)",
                  }}
                >
                  Tisztított szelep
                </h4>
                <p className="mt-2 text-sm text-zinc-300">
                  Endoszkópos közelkép — mindkét szelep karc nélkül, makulátlan.
                </p>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* VIDEO — Intake manifold deep cleaning as part of the walnut shell service */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="diohej-video-section"
      >
        {/* Amber glow — ties to walnut aesthetic */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div
            className="absolute top-10 right-1/4 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.06]"
            style={{ background: "radial-gradient(circle, #D97706 0%, transparent 70%)" }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Videón — élőben</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
                data-testid="diohej-video-headline"
              >
                A szívósor is
                <br />
                <span className="text-[#D97706]">makulátlanra tisztul</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-300 leading-relaxed">
              Dióhéjas tisztítás alkalmával a szívósort is alaposan
              megszabadítjuk a korom lerakódásoktól.
            </p>
          </div>

          {/* Video player */}
          <div
            className="relative bg-black border border-white/10 overflow-hidden shadow-2xl"
            data-testid="diohej-video-player"
          >
            <div className="m-stripe absolute top-0 left-0 right-0 h-[2px] z-10" />

            <div className="relative aspect-video">
              <video
                className="w-full h-full object-cover"
                src="/videos/diohejas-szivosor.mp4"
                poster="/videos/diohejas-szivosor-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls
                data-testid="diohej-video-element"
              >
                Böngészője nem támogatja a HTML5 videót.
              </video>

              {/* Top-left label overlay */}
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-white/10 px-3 py-1.5 pointer-events-none">
                <span
                  className="w-2 h-2 rounded-full bg-[#D97706] animate-pulse"
                  aria-hidden="true"
                />
                <span
                  className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Szívósor-tisztítás · Dióhéjas szerviz
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="diohej-comparison-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Vegyszer vs. Dióhéj</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Vegyszeres tisztítás
                <br />
                <span style={{ color: ACCENT }}>vs.</span> Dióhéj-fúvatás.
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              A dobozos vegyszerek és benzinadalékok mítosza ellenére: a kemény
              koksz-réteget csak mechanikusan lehet eltávolítani.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-px bg-white/5 border border-white/5">
            <div className="bg-[#0d0d0d] p-8 sm:p-10" data-testid="diohej-compare-old">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 flex items-center justify-center bg-zinc-700/30 border border-zinc-600/40">
                  <XCircle className="w-5 h-5 text-zinc-400" strokeWidth={1.6} />
                </div>
                <h3
                  className="text-xl sm:text-2xl font-bold uppercase text-zinc-400"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Vegyszeres / adalékos
                </h3>
              </div>
              <ul className="space-y-3">
                {COMPARISON.vegyszer.map((c) => (
                  <li
                    key={c.text}
                    className="flex items-start gap-3 text-sm text-zinc-500 leading-relaxed line-through decoration-zinc-700/50"
                  >
                    <XCircle className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="relative bg-[#0d0d0d] p-8 sm:p-10"
              data-testid="diohej-compare-walnut"
              style={{
                background: `linear-gradient(135deg, ${ACCENT}10 0%, #0d0d0d 60%)`,
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-11 h-11 flex items-center justify-center border"
                  style={{
                    backgroundColor: `${ACCENT}15`,
                    borderColor: `${ACCENT}50`,
                  }}
                >
                  <CheckCircle2 className="w-5 h-5" style={{ color: ACCENT }} strokeWidth={1.8} />
                </div>
                <h3
                  className="text-xl sm:text-2xl font-bold uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Dióhéj-fúvatás
                </h3>
              </div>
              <ul className="space-y-3">
                {COMPARISON.diohej.map((c) => (
                  <li
                    key={c.text}
                    className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed"
                  >
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} strokeWidth={2} />
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="diohej-pillars-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">A dióhéj-fúvatás előnyei</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Miért ez
                <br />
                <span style={{ color: ACCENT }}>az egyetlen megoldás.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-[#0d0d0d] hover:bg-[#121212] transition-colors p-7"
                  data-testid={`diohej-pillar-${p.title}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-6 border"
                    style={{
                      backgroundColor: `${ACCENT}10`,
                      borderColor: `${ACCENT}40`,
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: ACCENT }} strokeWidth={1.5} />
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="diohej-process-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">A folyamat</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                4 lépés —
                <br />
                <span style={{ color: ACCENT }}>tiszta motor.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {PROCESS.map((p) => (
              <div
                key={p.n}
                className="bg-[#0d0d0d] p-7 relative"
                data-testid={`diohej-process-${p.n}`}
              >
                <div
                  className="text-5xl font-black tracking-tight"
                  style={{ fontFamily: "Barlow Condensed", color: ACCENT }}
                >
                  {p.n}
                </div>
                <h3
                  className="mt-3 text-lg font-bold uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="diohej-when-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Mikor van szükség rá?</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Ha ezt érzi —
                <br />
                <span style={{ color: ACCENT }}>itt az idő.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {WHEN.map((w) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  className="service-card flex flex-col"
                  data-testid={`diohej-when-${w.title}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-12 h-12 flex items-center justify-center border"
                      style={{
                        backgroundColor: `${ACCENT}10`,
                        borderColor: `${ACCENT}40`,
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: ACCENT }} strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {w.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING NOTE */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="diohej-pricing-note"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Wrench
            className="w-10 h-10 mx-auto mb-6"
            style={{ color: ACCENT }}
            strokeWidth={1.4}
          />
          <h3
            className="font-black uppercase text-white leading-[0.95]"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
            }}
          >
            Dióhéjas tisztítás —
            <br />
            átlátható kiindulóáron.
          </h3>

          {/* Price badge */}
          <div className="mt-8 inline-flex items-baseline gap-2 bg-white/95 text-[#0A0A0A] px-6 py-4">
            <span
              className="font-black tracking-tight leading-none"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
              }}
            >
              79 000 Ft
            </span>
            <span
              className="text-sm sm:text-base font-bold uppercase tracking-[0.2em] leading-none text-zinc-600"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              -tól
            </span>
          </div>

          <p className="mt-7 text-zinc-400 leading-relaxed max-w-2xl mx-auto">
            A pontos ár a motortól függ (4 vagy 6 henger, hozzáférhetőség,
            szívócsonk-bontás komplexitása). Hívjon vagy küldjön üzenetet —
            autója adatainak ismeretében pontos árajánlatot adunk.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/?service=Teljesk%C3%B6r%C5%B1%20szerviz#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="diohej-pricing-cta-booking"
            >
              Ajánlatkérés <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="diohej-pricing-cta-phone"
            >
              <Phone className="w-4 h-4" /> Hívás most
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="diohej-faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <span className="section-eyebrow mb-4">GYIK</span>
          <h2
            className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95] mb-12"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
            }}
          >
            Gyakori kérdések.
          </h2>

          <div className="space-y-px bg-white/5 border border-white/5">
            {FAQ.map((f) => (
              <details
                key={f.q}
                className="group bg-[#0d0d0d] hover:bg-[#121212] transition-colors"
                data-testid={`diohej-faq-${f.q}`}
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="text-white font-semibold pr-4">{f.q}</span>
                  <span
                    className="w-7 h-7 flex items-center justify-center border border-white/20 text-lg leading-none transition-transform group-open:rotate-45 shrink-0"
                    style={{ color: ACCENT }}
                  >
                    +
                  </span>
                </summary>
                <div className="px-6 pb-6 text-sm text-zinc-400 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="diohej-cta-banner"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative bg-gradient-to-br from-[#0d0d0d] to-[#0a0a0a] border border-white/10 p-10 sm:p-14 overflow-hidden">
            <div
              className="absolute -top-24 -right-24 w-72 h-72 blur-3xl pointer-events-none"
              style={{ backgroundColor: `${ACCENT}40` }}
            />
            <div className="m-stripe absolute top-0 left-0 right-0 h-1" />

            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <span
                  className="text-[0.7rem] font-bold uppercase tracking-[0.24em]"
                  style={{ fontFamily: "Barlow Condensed", color: ACCENT }}
                >
                  Ne várjon a hibakódra
                </span>
                <h3
                  className="mt-3 font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                  }}
                >
                  Adja vissza autójának
                  <br />a gyári teljesítményt.
                </h3>
                <p className="mt-4 text-zinc-400 max-w-xl">
                  Egy 2–4 órás szerviz visszaadja a koksztól megvont
                  lóerőt, csökkenti a fogyasztást és megelőzi a több százezres
                  hengerfej-felújítást.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                <Link
                  to="/?service=Teljesk%C3%B6r%C5%B1%20szerviz#foglalas"
                  className="btn-primary inline-flex items-center w-full md:w-auto justify-center"
                  data-testid="diohej-cta-banner-booking"
                >
                  Időpontot foglalok <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <a
                  href="tel:+36308637476"
                  className="btn-outline inline-flex items-center gap-2 w-full md:w-auto justify-center"
                  data-testid="diohej-cta-banner-phone"
                >
                  <Phone className="w-4 h-4" /> Hívás most
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-center">
            <a
              href="tel:+36308637476"
              className="bg-[#0e0e0e] border border-white/5 hover:border-white/20 transition-colors p-5"
              data-testid="diohej-contact-phone"
            >
              <Phone className="w-5 h-5 mx-auto mb-2" style={{ color: ACCENT }} strokeWidth={1.6} />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">Telefon</div>
              <div className="text-white font-medium mt-1">+36 30 863 7476</div>
            </a>
            <a
              href="mailto:autoszerelem@gmail.com"
              className="bg-[#0e0e0e] border border-white/5 hover:border-white/20 transition-colors p-5"
              data-testid="diohej-contact-email"
            >
              <Mail className="w-5 h-5 mx-auto mb-2" style={{ color: ACCENT }} strokeWidth={1.6} />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">E-mail</div>
              <div className="text-white font-medium mt-1">autoszerelem@gmail.com</div>
            </a>
            <div className="bg-[#0e0e0e] border border-white/5 p-5">
              <CheckCircle2 className="w-5 h-5 mx-auto mb-2" style={{ color: ACCENT }} strokeWidth={1.6} />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">Cím</div>
              <div className="text-white font-medium mt-1">4031 Debrecen, Vadvirág u. 15/D</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          data-testid="diohej-lightbox"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Bezárás"
            data-testid="diohej-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxSrc}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
